function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
function withViewTransition(update) {
  if (document.startViewTransition && document.body.classList.contains('animations-true')) {
    document.startViewTransition(update);
  } else {
    update();
  }
}
var dispatchCustomEvent = function dispatchCustomEvent(eventName) {
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var detail = {
    detail: data
  };
  var event = new CustomEvent(eventName, data ? detail : null);
  document.dispatchEvent(event);
};

/**
 *  @object
 *  @function OverlayManager
 *  @description Stack-based overlay manager for side panels, drawers, and modals.
 */
const OverlayManager = {
  stack: [],
  beforeCloseHandlers: new Map(),
  extraBodyClassMap: new Map(),
  lastFocused: new Map(),
  _trapHandler: null,
  focusableSelector: 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  cc: null,

  trapFocus(e) {
    if (e.key !== 'Tab' || !this.stack.length) return;
    const panel = this.stack[this.stack.length - 1];
    const focusable = Array.from(panel.querySelectorAll(this.focusableSelector)).filter((el) => el.getClientRects().length);
    if (!focusable.length) {
      e.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const inside = panel.contains(document.activeElement);
    if (e.shiftKey) {
      if (!inside || document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else if (!inside || document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  },

  getCC() {
    if (!this.cc) {
      this.cc = document.querySelector('.click-capture');
    }
    return this.cc;
  },

  updateCCZIndex() {
    const cc = this.getCC();
    if (!cc) return;
    if (this.stack.length > 1) {
      const topPanel = this.stack[this.stack.length - 1];
      const topZ = parseInt(getComputedStyle(topPanel).zIndex, 10) || 90;
      cc.style.zIndex = topZ - 1;
    } else {
      cc.style.zIndex = '';
    }
  },

  open(panel, extraBodyClasses) {
    if (!panel) return;
    // If already in stack, move to top; only remember the opener on first open
    const index = this.stack.indexOf(panel);
    if (index === -1) {
      this.lastFocused.set(panel, document.activeElement);
    } else {
      this.stack.splice(index, 1);
    }
    if (!this.stack.length) {
      if (!this._trapHandler) this._trapHandler = this.trapFocus.bind(this);
      document.addEventListener('keydown', this._trapHandler);
    }
    this.stack.push(panel);
    if (extraBodyClasses && extraBodyClasses.length) {
      this.extraBodyClassMap.set(panel, extraBodyClasses);
      document.body.classList.add(...extraBodyClasses);
    }
    panel.classList.add('active');
    panel.inert = false;
    document.body.classList.add('open-cc');
    this.updateCCZIndex();
    const closeBtn = panel.querySelector('.side-panel-close');
    if (closeBtn) closeBtn.focus();
    dispatchCustomEvent('panel:open', { panel });
  },

  close(panel) {
    if (!panel) return;
    const index = this.stack.indexOf(panel);
    if (index > -1) this.stack.splice(index, 1);
    const extraClasses = this.extraBodyClassMap.get(panel);
    if (extraClasses) {
      document.body.classList.remove(...extraClasses);
      this.extraBodyClassMap.delete(panel);
    }
    if (!this.stack.length) {
      document.body.classList.remove('open-cc');
      if (this._trapHandler) {
        document.removeEventListener('keydown', this._trapHandler);
      }
    }
    this.updateCCZIndex();
    panel.classList.remove('active');
    panel.inert = true;

    const lastFocused = this.lastFocused.get(panel);
    if (!this.stack.length && lastFocused && lastFocused.isConnected) {
      lastFocused.focus();
    }
    this.lastFocused.delete(panel);

    dispatchCustomEvent('panel:closed', { panel });

    const handler = this.beforeCloseHandlers.get(panel);
    if (handler) handler();
  },

  closeLast() {
    const panel = this.stack[this.stack.length - 1];
    if (panel) this.close(panel);
  },

  registerBeforeClose(panel, handler) {
    this.beforeCloseHandlers.set(panel, handler);
  },

  isOpen(panel) {
    return this.stack.includes(panel);
  },

  hasOpen() {
    return this.stack.length > 0;
  }
};

/**
 *  Deferred video loading — stamps <template class="background-video__deferred">
 *  into the live DOM when the parent nears the viewport.
 */
document.querySelectorAll('.background-video__deferred').forEach(function (template) {
  if (template.closest('background-video') || template.closest('slide-show.main-slideshow') || template.closest('image-with-text')) return;
  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        observer.disconnect();
        var clone = template.content.cloneNode(true);
        const video = clone.querySelector('video');
        template.replaceWith(clone);

        /* 修改某些模块不显示poster的情况 */
        const poster = video.poster;

          if (poster) {
            video.poster = '';
            video.poster = poster;
          }
      }
    });
  }, { rootMargin: '100px' });
  observer.observe(template.parentElement || template);
});

window.recentlyViewedIds = [];

/**
 *  @class
 *  @function Quantity
 */
if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.qty');
      this.step = this.input.getAttribute('step');
      this.changeEvent = new Event('change', {
        bubbles: true
      });
      // Create buttons
      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');

      // Add functionality to buttons
      this.subtract.addEventListener('click', () => this.change_quantity(-1 * this.step));
      this.add.addEventListener('click', () => this.change_quantity(1 * this.step));

    }
    connectedCallback() {
      this.classList.add('buttons_added');
      this.validateQtyRules();
    }
    change_quantity(change) {
      // Get current value
      let quantity = Number(this.input.value);

      // Ensure quantity is a valid number
      if (isNaN(quantity)) quantity = 1;

      // Check for min & max
      if (this.input.getAttribute('min') > (quantity + change)) {
        return;
      }
      if (this.input.getAttribute('max')) {
        if (this.input.getAttribute('max') < (quantity + change)) {
          return;
        }
      }
      // Change quantity
      quantity += change;

      // Ensure quantity is always a number
      quantity = Math.max(quantity, 1);

      // Output number
      this.input.value = quantity;

      this.input.dispatchEvent(this.changeEvent);

      this.validateQtyRules();
    }
    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (this.input.min) {
        const min = parseInt(this.input.min);
        this.subtract.classList.toggle('disabled', value <= min);
      }
      if (this.input.max) {
        const max = parseInt(this.input.max);
        this.add.classList.toggle('disabled', value >= max);
      }
    }
  }
  customElements.define('quantity-selector', QuantityInput);
}

/**
 *  @class
 *  @function ProductCard
 */

if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this.swatches = this.querySelector('.product-card-swatches');
      this.secondary = this.querySelector('.product-secondary-image');
      this.image = this.secondary || this.querySelector('.product-primary-image');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');
      this.size_options = this.querySelector('.product-card-sizes');
      
      this.video = this.querySelector('.background-video__controls');
      this.toggle = this.querySelector('.background-video__controls button');
    }
    connectedCallback() {
      if (this.swatches) {
        this.enableSwatches(this.swatches, this.image);
      }
      if (this.quick_add) {
        this.enableQuickAdd();
      }
      if (this.secondary) {
        window.addEventListener('load', (event) => {
          lazySizes.loader.unveil(this.secondary);
        });
      }
      if (this.size_options) {
        this.enableSizeOptions();
      }

      // Video Controls.
      if(this.video){
        this.paused = this.video.dataset.pausePlay || false;
      }
			if (this.toggle) {        
				this.toggle.addEventListener('click', this.setupControlEvents.bind(this));
			}
      // Video Support.
			this.video_container = this.querySelector('.background-video__iframe');

    }
    enableSwatches(swatches, image) {
      let swatch_list = swatches.querySelectorAll('.product-card-swatch'),
        org_srcset = image ? image.dataset.srcset : '';
      this.color_index = this.swatches.dataset.index;

      swatch_list.forEach((swatch, index) => {
        window.addEventListener('load', (event) => {
          let image = new Image();
          image.srcset = swatch.dataset.srcset;
          lazySizes.loader.unveil(image);
        });
        swatch.addEventListener('mouseover', () => {

          [].forEach.call(swatch_list, function (el) {
            el.classList.remove('active');
          });
          if (image) {
            if (swatch.dataset.srcset) {
              image.setAttribute('srcset', swatch.dataset.srcset);
            } else {
              image.setAttribute('srcset', org_srcset);
            }
          }
          if (this.size_options) {
            this.current_options[this.color_index] = swatch.querySelector('span').innerText;
            this.updateMasterId();
          }
          swatch.classList.add('active');
        });
        swatch.addEventListener('click', function (evt) {
          window.location.href = this.dataset.href;
          evt.preventDefault();
        });
      });
    }
    enableQuickAdd() {
      this.quick_add.addEventListener('click', this.quickAdd.bind(this));
    }
    enableSizeOptions() {
      let size_list = this.size_options.querySelectorAll('.product-card-sizes--size'),
        featured_image = this.querySelector('.product-featured-image'),
        has_hover = featured_image.classList.contains('thb-hover'),
        size_parent = this.size_options.parentElement;

      this.size_index = this.size_options.dataset.index;

      this.current_options = this.size_options.dataset.options.split(',');

      this.updateMasterId();

      size_parent.addEventListener('mouseenter', () => {
        if (has_hover) {
          featured_image.classList.remove('thb-hover');
        }
      }, {
        passive: true
      });
      size_parent.addEventListener('mouseleave', () => {
        if (has_hover) {
          featured_image.classList.add('thb-hover');
        }
      }, {
        passive: true
      });
      size_list.forEach((size) => {
        size.addEventListener('click', (evt) => {
          evt.preventDefault();

          if (size.classList.contains('is-disabled')) {
            return;
          }
          this.current_options[this.size_index] = size.querySelector('span').innerText;
          this.updateMasterId();

          size.classList.add('loading');
          size.setAttribute('aria-disabled', true);
          const config = {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/javascript'
            }
          };
          let formData = new FormData();

          formData.append('id', this.currentVariant.id);
          formData.append('quantity', 1);
          formData.append('sections', this.getSectionsToRender().map((section) => section.section));
          formData.append('sections_url', window.location.pathname);

          config.body = formData;

          fetch(`${theme.routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                return;
              }
              this.renderContents(response);

              dispatchCustomEvent('cart:item-added', {
                product: response.hasOwnProperty('items') ? response.items[0] : response
              });
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {
              size.classList.remove('loading');
              size.removeAttribute('aria-disabled');
            });
        });
      });
    }




    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.current_options[index] === option;
        }).includes(false);
      });
      setTimeout(() => {
        this.setDisabled();
      }, 100);
    }
    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
    setDisabled() {
      const variant_data = this.getVariantData();

      if (variant_data) {

        if (this.currentVariant) {
          const selected_options = this.currentVariant.options.map((value, index) => {
            return {
              value,
              index: `option${index + 1}`
            };
          });

          const available_options = this.createAvailableOptionsTree(variant_data, selected_options);

          const fieldset_options = Object.values(available_options)[this.size_index];
          if (fieldset_options) {
            if (this.size_options.querySelectorAll('.product-card-sizes--size').length) {
              this.size_options.querySelectorAll('.product-card-sizes--size').forEach((input, input_i) => {
                input.classList.toggle('is-disabled', fieldset_options[input_i].isUnavailable);
              });
            }
          }
        } else {
          if (this.size_options.querySelectorAll('.product-card-sizes--size').length) {
            this.size_options.querySelectorAll('.product-card-sizes--size').forEach((input, input_i) => {
              input.classList.add('is-disabled');
            });
          }
        }

      }
      return true;
    }
    createAvailableOptionsTree(variant_data, selected_options) {
      // Reduce variant array into option availability tree
      return variant_data.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = {
              value: variant[index],
              isUnavailable: true
            };
            options[index].push(entry);
          }

          // Check how many selected option values match a variant
          const countVariantOptionsThatMatchCurrent = selected_options.reduce((count, {
            value,
            index
          }) => {
            return variant[index] === value ? count + 1 : count;
          }, 0);

          // Only enable an option if an available variant matches all but one current selected value
          if (countVariantOptionsThatMatchCurrent >= selected_options.length - 1) {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }

          // Make sure if a variant is unavailable, disable currently selected option
          if ((!this.currentVariant || !this.currentVariant.available) && selected_options.find((option) => option.value === entry.value && index === option.index)) {
            entry.isUnavailable = true;
          }

          // First option is always enabled
          if (index === 'option1') {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }
        });

        return options;
      }, {
        option1: [],
        option2: [],
        option3: []
      });
    }
    quickAdd(evt) {
      evt.preventDefault();
      if (this.quick_add.getAttribute('disabled') != undefined) {
        return;
      }
      this.quick_add.classList.add('loading');
      this.quick_add.setAttribute('aria-disabled', true);

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };

      let formData = new FormData();

      formData.append('id', this.quick_add.dataset.productId);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);

      config.body = formData;

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            return;
          }
          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.quick_add.classList.remove('loading');
          this.quick_add.removeAttribute('aria-disabled');
        });

      return false;
    }
    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.cart-items--items'
      },
      {
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form--cart-collaterals'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }));


      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        cartDrawer.notesToggle();
        cartDrawer.discountToggle();
        cartDrawer.updateFreeShipping();
        OverlayManager.open(cartDrawer, ['open-cart']);
        const recs = cartDrawer.querySelector('.product-recommendations--full');
        if (recs) recs.classList.add('active');
        dispatchCustomEvent('cart-drawer:open');
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    /* 视频的控制 */
    setupControlEvents() {
			if (this.paused) {

				this.videoPlay();
				if (this.toggle) {
					this.toggle.classList.remove('paused');
				}
			} else {
				this.videoPause();
				if (this.toggle) {
					this.toggle.classList.add('paused');
				}
			}
		}
		videoPlay() {
			setTimeout(() => {
				if (this.video_container.dataset.provider === 'youtube') {
					this.video_container.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
						event: "command",
						func: "playVideo",
						args: ""
					}), "*");
				} else if (this.video_container.dataset.provider === 'vimeo') {
					this.video_container.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
						method: "play"
					}), "*");
				} else if (this.video_container.dataset.provider === 'hosted') {
					this.video_container.querySelector('video').play();
				}

				this.paused = false;
			}, 10);
		}
		videoPause() {
			setTimeout(() => {
				if (this.video_container.dataset.provider === 'youtube') {
					this.video_container.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
						event: "command",
						func: "pauseVideo",
						args: ""
					}), "*");
				} else if (this.video_container.dataset.provider === 'vimeo') {
					this.video_container.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
						method: "pause"
					}), "*");
				} else if (this.video_container.dataset.provider === 'hosted') {
					this.video_container.querySelector('video').pause();
				}
				this.paused = true;
			}, 10);
		}

  }
  customElements.define('product-card', ProductCard);
}



/**
 *  @class
 *  @function PanelClose
 */
if (!customElements.get('side-panel-close')) {
  class PanelClose extends HTMLElement {

    constructor() {
      super();
      this.panel = this.closest('.side-panel');
    }

    connectedCallback() {
      const cc = document.querySelector('.click-capture');

      this.addEventListener('click', (e) => this.handleClose(e));
      this.addEventListener('keydown', (e) => this.handleClose(e));

      if (cc && !cc.dataset.overlayBound) {
        cc.dataset.overlayBound = '1';
        cc.addEventListener('click', () => OverlayManager.closeLast());
      }

      document.addEventListener('panel:close', () => OverlayManager.closeLast());
    }

    handleClose(e) {
      if (e.type === 'keydown' && e.code && e.code.toUpperCase() !== 'ENTER') return;
      if (this.panel) {
        OverlayManager.close(this.panel);
      } else {
        OverlayManager.closeLast();
      }
    }
  }
  customElements.define('side-panel-close', PanelClose);

  document.addEventListener('keyup', (e) => {
    if (e.code && e.code.toUpperCase() === 'ESCAPE') {
      dispatchCustomEvent('panel:close');
    }
  });
}
/**
 *  @class
 *  @function CartDrawer
 */
class CartDrawer extends HTMLElement {

  constructor() {
    super();
  }

  connectedCallback() {
    this.button = document.getElementById('cart-drawer-toggle');

    // Add functionality to buttons
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      OverlayManager.open(this, ['open-cart']);
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      });
      dispatchCustomEvent('cart-drawer:open');
    });

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    document.addEventListener('cart:refresh', (event) => {
      this.refresh();
    });

    this.addEventListener('change', this.debouncedOnChange.bind(this));

    this.notesToggle();
    this.discountToggle();
    this.updateFreeShipping();

    OverlayManager.registerBeforeClose(this, () => {
      let notes = this.querySelector('#mini-cart-note');
      let discount = this.querySelector('#mini-cart-discount');
      if (notes) notes.classList.remove('active');
      if (discount) discount.classList.remove('active');
    });
  }
  onChange(event) {
    if (event.target.classList.contains('qty')) {
      this.updateQuantity(event.target.dataset.index, event.target.value);
    }
  }
  getSectionsToRender() {
    return [{
      id: 'Cart-Drawer',
      section: 'cart-drawer',
      selector: '.cart-drawer-inner'
    },
    {
      id: 'cart-drawer-toggle',
      section: 'cart-bubble',
      selector: '.thb-item-count'
    }];
  }
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
  termsCheckbox() {
    let terms_checkbox = this.querySelector('#CartDrawerTerms'),
      checkout_button = this.querySelector('.button.checkout');

    if (terms_checkbox && checkout_button) {
      terms_checkbox.setCustomValidity(theme.strings.requiresTerms);
      checkout_button.addEventListener('click', function (e) {
        if (!terms_checkbox.checked) {
          terms_checkbox.reportValidity();
          terms_checkbox.focus();
          e.preventDefault();
        }
      });
    }
  }
  notesToggle() {
    let notes_toggle = this.querySelector('#order-note-toggle');
    let notes_content = this.querySelector('#mini-cart-note');

    if (!notes_toggle || !notes_content) {
      return;
    }

    notes_toggle.addEventListener('click', (event) => {
      notes_content.classList.add('active');
    });
    notes_content.querySelectorAll('.button, .order-note-toggle__content-overlay').forEach((el) => {
      el.addEventListener('click', (event) => {
        notes_content.classList.remove('active');
        this.saveNotes();
      });
    });
  }
  discountToggle() {
    let discount_toggle = this.querySelector('#discount-toggle');
    let discount_content = this.querySelector('#mini-cart-discount');

    if (!discount_toggle || !discount_content) {
      return;
    }

    discount_toggle.addEventListener('click', (event) => {
      discount_content.classList.add('active');
    });
    discount_content.querySelectorAll('.discount-toggle__content-overlay').forEach((el) => {
      el.addEventListener('click', (event) => {
        discount_content.classList.remove('active');
      });
    });
  }
  saveNotes() {
    fetch(`${theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/json`
      },
      body: JSON.stringify({
        'note': document.getElementById('mini-cart__notes').value
      })
    });
  }
  updateFreeShipping() {
    const free_shipping = this.querySelector('.free-shipping');

    if (free_shipping) {
      let amount_text = free_shipping.querySelector('.free-shipping--text span');
      let total = parseInt(free_shipping.dataset.cartTotal, 10);
      let minimum = Math.round(parseInt(free_shipping.dataset.minimum, 10) * (Shopify.currency.rate || 1));
      let percentage = 1;

      if (total < minimum) {
        percentage = total / minimum;

        if (amount_text) {
          let remaining = minimum - total,
            format = window.theme.settings.money_with_currency_format || "${{amount}}";
          amount_text.innerHTML = formatMoney(remaining, format);
        }
      }

      free_shipping.style.setProperty('--percentage', percentage);
    }
  }
  updateQuantity(line, quantity) {
    this.querySelector(`#CartDrawerItem-${line}`)?.classList.add('thb-loading');
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    dispatchCustomEvent('line-item:change:start', {
      quantity: quantity
    });

    fetch(`${theme.routes.cart_change_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/json`
      },
      ...{
        body
      }
    })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        this.getSectionsToRender().forEach((section => {
          const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

          if (parsedState.sections) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          }
        }));

        this.notesToggle();
        this.discountToggle();
        this.updateFreeShipping();

         // ✅ 在这里广播 cart 变化,用于add on items的按钮状态变化
        dispatchCustomEvent('cart:change', {
        variantIds: parsedState.items.map(item => item.id.toString())
        });

        dispatchCustomEvent('line-item:change:end', {
          quantity: quantity,
          cart: parsedState
        });

        if (this.querySelector(`#CartDrawerItem-${line}`)) {
          this.querySelector(`#CartDrawerItem-${line}`).classList.remove('thb-loading');
        }
      });
  }
  refresh() {
    let sections = 'cart-drawer,cart-bubble';
    fetch(`${window.location.pathname}?sections=${sections}`)
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        this.getSectionsToRender().forEach((section => {
          const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector);
        }));

        this.notesToggle();
        this.discountToggle();
        this.updateFreeShipping();

         // ✅ 在这里广播 cart 变化,用于add on items的按钮状态变化
        dispatchCustomEvent('cart:change', {
        variantIds: parsedState.items.map(item => item.id.toString())
        });
      });
  }
}
customElements.define('cart-drawer', CartDrawer);

/**
 *  @class
 *  @function ModalDialog
 */
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.closeButton = this.querySelector('[id^="ModalClose-"]');

    if (this.closeButton) {
      this.closeButton.addEventListener(
        'click',
        this.hide.bind(this)
      );
      this.closeButton.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ENTER') {
          this.hide();
        }
      });
    }

    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') {
        this.hide();
      }
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    if (this.dataset.moved) return;
    this.moved = true;
    document.body.appendChild(this);

    //自定义tab切换事件
    this.addEventListener('click', (e) => {
      const tab = e.target.closest('.tab-item');
      if (tab && this.contains(tab)) {
        const tabId = tab.dataset.tab;
        if (tabId) {
          this.activateTab(tabId);
        }
      }
    });

  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');

    setTimeout(() => {
      this.querySelector('[role="dialog"]').focus();
    }, 100);

     // 切换对应 tab
    const targetTab = opener?.dataset?.tab; // 从按钮上读取 data-tab
    this.activateTab(targetTab);

  }

  activateTab(tabId) {
    const tabs = this.querySelectorAll('.tab-item'); // 你的 tab 按钮
    const panels = this.querySelectorAll('.tab-content'); // 对应 tab 内容区

    if (!tabs.length || !panels.length) return;
    tabs.forEach(tab => {
      if (tab.dataset.tab === tabId) {
        tab.classList.add('active');
      } else {
        tab.classList.remove('active');
      }
    });

    panels.forEach(panel => {
      if (panel.dataset.tab === tabId) {
        panel.classList.add('active');
      } else {
        panel.classList.remove('active');
      }
    });
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    this.querySelectorAll('.js-youtube').forEach((video) => {
      video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
    });
    this.querySelectorAll('.js-vimeo').forEach((video) => {
      video.contentWindow.postMessage('{"method":"pause"}', '*');
    });
    this.querySelectorAll('video').forEach((video) => video.pause());
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

/**
 *  @class
 *  @function ResizeSelect
 */
if (!customElements.get('resize-select')) {
  class ResizeSelect extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      this.addEventListener('change', this.resizeSelect.bind(this));
      window.addEventListener('load', this.resizeSelect.bind(this));
      const details = this.closest('details');
      if (details) {
        details.addEventListener('toggle', () => {
          if (details.open) this.resizeSelect();
        });
      }
      this.resizeSelect();
    }
    resizeSelect() {
      let tempOption = document.createElement('option');
      tempOption.textContent = this.select.selectedOptions[0].textContent;

      let tempSelect = document.createElement('select');
      tempSelect.style.cssText = 'visibility:hidden;position:fixed;pointer-events:none';
      tempSelect.appendChild(tempOption);
      this.after(tempSelect);

      requestAnimationFrame(() => {
        if (tempSelect.clientWidth > 0) {
          this.select.style.width = `${tempSelect.clientWidth + 10}px`;
        }
        tempSelect.remove();
      });
    }
  }
  customElements.define('resize-select', ResizeSelect);
}

/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get('quick-view')) {
  const _cache = new Map();

  class QuickView extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.drawer = document.getElementById('Product-Drawer');
      this.drawerContent = this.drawer.querySelector('#Product-Drawer-Content');

      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);

      OverlayManager.registerBeforeClose(this.drawer, () => {
        setTimeout(() => {
          if (!this.drawer.classList.contains('active')) {
            this.drawerContent.innerHTML = '';
          }
        }, 260);
      });
    }
    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }
    _onClick(e) {
      e.preventDefault();
      let productHandle = this.dataset.productHandle;
      if (!productHandle || this.classList.contains('loading')) {
        return;
      }

      let href = `${theme.routes.root_url}/products/${productHandle}?view=quick-view`;
      // remove double `/` in case shop might have /en or language in URL
      href = href.replace('//', '/');

      if (_cache.has(productHandle)) {
        this.renderQuickview(_cache.get(productHandle), href, productHandle);
        return;
      }

      this.classList.add('loading');
      fetch(href)
        .then((response) => response.text())
        .then((text) => {
          this.classList.remove('loading');
          const sectionInnerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#Product-Drawer-Content').innerHTML;

          _cache.set(productHandle, sectionInnerHTML);
          this.renderQuickview(sectionInnerHTML, href, productHandle);
        })
        .catch(() => {
          this.classList.remove('loading');
        });
    }
    _reloadScripts(container) {
      let scripts = container.querySelectorAll('script');
      let head = document.head;
      scripts.forEach((oldScript) => {
        let newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        if (oldScript.src) {
          if (document.querySelector(`script[src="${oldScript.src}"]`)) {
            return;
          }
        } else {
          newScript.textContent = oldScript.textContent;
        }
        if (oldScript.parentNode) {
          oldScript.parentNode.replaceChild(newScript, oldScript);
        } else {
          head.appendChild(newScript);
        }
      });
    }
    renderQuickview(sectionInnerHTML, href, productHandle) {
      if (!sectionInnerHTML) {
        return;
      }

      this.drawerContent.innerHTML = sectionInnerHTML;

      this._reloadScripts(this.drawerContent);

      OverlayManager.open(this.drawer);

      requestAnimationFrame(() => {
        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
        window.dispatchEvent(new Event('resize'));
      });

      dispatchCustomEvent('quick-view:open', {
        productUrl: href,
        productHandle: productHandle
      });
      addIdToRecentlyViewed(productHandle);
    }
  }
  customElements.define('quick-view', QuickView);
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get('collapsible-row')) {
  // https://css-tricks.com/how-to-animate-the-details-element/
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();

      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.collapsible__content');

      // Store the animation object (so we can cancel it if needed)
      this.animation = null;
      // Store if the element is closing
      this.isClosing = false;
      // Store if the element is expanding
      this.isExpanding = false;
    }
    connectedCallback() {
      this.setListeners();
    }
    setListeners() {
      this.querySelector('summary').addEventListener('click', (e) => this.onClick(e));
    }
    instantClose() {
      this.tl.timeScale(10).reverse();
    }
    animateClose() {
      this.tl.timeScale(3).reverse();
    }
    animateOpen() {
      this.tl.timeScale(1).play();
    }
    onClick(e) {
      // Stop default behaviour from the browser
      e.preventDefault();
      // Add an overflow on the <details> to avoid content overflowing
      this.details.style.overflow = 'hidden';
      // Check if the element is being closed or is already closed
      if (this.isClosing || !this.details.open) {
        this.open();
        // Check if the element is being openned or is already open
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }
    shrink() {
      // Set the element as "being closed"
      this.isClosing = true;

      // Store the current height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the height of the summary
      const endHeight = `${this.summary.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 250,
        easing: 'ease'
      });

      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(false);
      // If the animation is cancelled, isClosing variable is set to false
      this.animation.oncancel = () => this.isClosing = false;
    }

    open() {
      // Apply a fixed height on the element
      this.details.style.height = `${this.details.offsetHeight}px`;
      // Force the [open] attribute on the details element
      this.details.open = true;
      // Wait for the next frame to call the expand function
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      // Set the element as "being expanding"
      this.isExpanding = true;
      // Get the current fixed height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the open height of the element (summary height + content height)
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 400,
        easing: 'ease-out'
      });
      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(true);
      // If the animation is cancelled, isExpanding variable is set to false
      this.animation.oncancel = () => this.isExpanding = false;
    }

    onAnimationFinish(open) {
      // Set the open attribute based on the parameter
      this.details.open = open;
      // Clear the stored animation
      this.animation = null;
      // Reset isClosing & isExpanding
      this.isClosing = false;
      this.isExpanding = false;
      // Remove the overflow hidden and the fixed height
      this.details.style.height = this.details.style.overflow = '';
    }
  }
  customElements.define('collapsible-row', CollapsibleRow);
}

/**
 *  @function addIdToRecentlyViewed
 */
function addIdToRecentlyViewed(handle) {

  if (!handle) {
    let product = document.querySelector('.thb-product-detail');

    if (product) {
      handle = product.dataset.handle;
    }
  }
  if (!handle) {
    return;
  }
  if (window.localStorage) {
    let recentIds = window.localStorage.getItem('recently-viewed');
    if (recentIds && typeof (recentIds) !== undefined) {
      window.recentlyViewedIds = JSON.parse(recentIds);
    }
  }
  // Remove current product if already in recently viewed array
  var i = window.recentlyViewedIds.indexOf(handle);

  if (i > -1) {
    window.recentlyViewedIds.splice(i, 1);
  }

  // Add id to array
  window.recentlyViewedIds.unshift(handle);

  if (window.localStorage) {
    window.localStorage.setItem('recently-viewed', JSON.stringify(window.recentlyViewedIds));
  }
}

/**
 *  Animations
 */
ScrollTrigger.batch('.animations-true .products .gallery--item figure, .animations-true .product-card .product-featured-image-link', {
  start: "top 90%",
  onEnter: (elements, triggers) => {
    gsap.to(elements, { scale: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
  }
});
ScrollTrigger.batch('.animations-true .multicolumn--column-inner', {
  start: "top 90%",
  onEnter: (elements, triggers) => {
    gsap.to(elements, { y: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
  }
});
ScrollTrigger.batch('.animations-true .blog-post .featured-image--link', {
  start: "top 90%",
  onEnter: (elements, triggers) => {
    gsap.to(elements, { scale: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
  }
});

/* 自定义js */
/**
 *  @class
 *  @function LoopTimer
 */
if (!customElements.get('loop-timer')) {
class LoopTimer extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // 获取模式：'daily'（当天结束）或 'fixed'（基于startTime循环），默认为 'fixed'
    this.mode = this.dataset.mode || 'fixed';
    
    // 获取循环时长（小时），默认为3小时
    this.durationHours = parseInt(this.dataset.duration) || 3;
    this.durationMs = this.durationHours * 60 * 60 * 1000;
    
    // 获取开始时间（ISO格式或时间戳），默认为当前时间
    this.startTime = this.dataset.startTime || new Date().toISOString();
    
    // 解析开始时间
    this.referenceTime = this.parseStartTime(this.startTime);
    
    this.startTimer();
    this._interval = setInterval(this.startTimer.bind(this), 1000);
  }

  parseStartTime(timeString) {
    // 如果是时间戳
    if (/^\d+$/.test(timeString)) {
      return parseInt(timeString);
    }
    
    // 如果是ISO格式时间
    const date = new Date(timeString);
    return date.getTime();
  }

  startTimer() {
    let timeDifference;
    
    if (this.mode === 'daily') {
      // 当天结束倒计时模式
      timeDifference = this.getDailyRemainingTime();
    } else {
      // 基于开始时间的循环倒计时模式
      timeDifference = this.getFixedLoopRemainingTime();
    }

    const hours = Math.floor(timeDifference / (60 * 60 * 1000));
    const mins = Math.floor((timeDifference % (60 * 60 * 1000)) / (60 * 1000));
    const secs = Math.floor((timeDifference % (60 * 1000)) / 1000);

    this.updateTimerDisplay(hours, mins, secs);
  }

  getFixedLoopRemainingTime() {
  const now = Date.now();
  const elapsed = now - this.referenceTime;

  if (elapsed < 0) {
    // startTime 在未来 → 倒计时从 duration 开始
    return this.durationMs;
  }

  // 计算在当前循环周期中的位置
  const positionInCycle = elapsed % this.durationMs;
  const remaining = this.durationMs - positionInCycle;

  return remaining;
}

  getDailyRemainingTime() {
    const offset = this.getTimezoneOffset();
    const now = new Date();

    // 当前 UTC 时间
    const utcNow = new Date(now.getTime() + now.getTimezoneOffset() * 60 * 1000);

    // 当前目标时区时间
    const targetNow = new Date(utcNow.getTime() + offset * 60 * 60 * 1000);

    // 当天结束时间（目标时区当天 24:00）
    const endOfDayTarget = new Date(targetNow);
    endOfDayTarget.setHours(24, 0, 0, 0);

    // 剩余时间（毫秒）
    return endOfDayTarget - targetNow;
  }

  getTimezoneOffset() {
    let timezone = this.dataset.timezone;

    // 未设置或 null -> 使用本地时区
    if (!timezone || timezone.toLowerCase() === "auto" || timezone.toLowerCase() === "null") {
      return -new Date().getTimezoneOffset() / 60;
    }

    const offset = parseInt(timezone, 10);
    return isNaN(offset) ? 0 : offset;
  }

  updateTimerDisplay(hours, mins, secs) {
    this.setDigits('.hours-num', hours);
    this.setDigits('.minutes-num', mins);
    this.setDigits('.seconds-num', secs);
  }

  setDigits(selector, value) {
    const element = this.querySelector(selector);
    if (!element) return;

    const str = String(value).padStart(2, '0');
    const [tens, units] = str;

    element.innerHTML = `
      <span class="digit tens">${tens}</span>
      <span class="digit units">${units}</span>
    `;
  }

  // 重置计时器（使用新的开始时间）
  resetTimer(newStartTime = null) {
    if (newStartTime) {
      this.startTime = newStartTime;
      this.referenceTime = this.parseStartTime(this.startTime);
    }
    this.startTimer();
  }

  // 更新设置
  updateSettings(mode, durationHours, timezone, startTime = null) {
    if (this._interval) clearInterval(this._interval);
    
    if (mode) this.dataset.mode = mode;
    if (durationHours) this.dataset.duration = durationHours;
    if (timezone) this.dataset.timezone = timezone;
    if (startTime) this.dataset.startTime = startTime;
    
    this.init();
  }

  disconnectedCallback() {
    if (this._interval) clearInterval(this._interval);
  }
}

customElements.define('loop-timer', LoopTimer);
}


/* 自定义横向滑动模块 */

if (!customElements.get('horizontal-scroll')) {
class HorizontalScroll extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[data-slider]');
    this.nextButton = this.querySelector('[data-next]');
    this.prevButton = this.querySelector('[data-prev]');
    this.parentGrid = this.closest('.add-on-items-grid');
   
  }

  connectedCallback() {
    if (!this.slider) return;

    this.nextScroll = this.parentGrid.querySelector('[data-scroll-next]');

    this.nextButton?.addEventListener('click', this.onNextClick);
    this.prevButton?.addEventListener('click', this.onPrevClick);
    this.nextScroll?.addEventListener('click', this.onScrollNextClick);

  }

  disconnectedCallback() {
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.prevButton?.removeEventListener('click', this.onPrevClick);
    this.nextScroll?.removeEventListener('click', this.onScrollNextClick);
  }

  getItemWidth() {
    const item = this.slider.querySelector('.item');
    return item ? item.offsetWidth + 15 : 0;
  }

  onNextClick = () => {
    this.slider.scrollBy({
      left: this.getItemWidth() * 2,
      behavior: 'smooth',
    });
  };

  onPrevClick = () => {
    this.slider.scrollBy({
      left: -this.getItemWidth() * 2,
      behavior: 'smooth',
    });
  };

  onScrollNextClick = () => {
    this.slider.scrollBy({
      left: this.slider.clientWidth,
      behavior: 'smooth',
    });
  };

}

customElements.define('horizontal-scroll', HorizontalScroll);
}

/* 
* add to cart

 */

const PRODUCT_CARD_LOCK_KEY = 'product_card_added_variants';

if (!customElements.get('add-to-cart')) {
  class AddToCart extends HTMLElement {
    constructor() {
      super();

      this.variantId = String(this.dataset.productId);
      this.enableLockAfterAdded = this.hasAttribute('data-lock-after-added');
    }

    connectedCallback() {
      this.updateButtonState(this.isAdded());

      if (this.enableLockAfterAdded) {
        document.addEventListener('cart:item-added', this.handleItemAdded);
      }

      document.addEventListener('cart:change', this.handleCartChange);
    }

    disconnectedCallback() {
      if (this.enableLockAfterAdded) {
        document.removeEventListener('cart:item-added', this.handleItemAdded);
      }

  
      document.removeEventListener('cart:change', this.handleCartChange);//当cart.js的cart:change触发
    }

    /**
     * 商品成功加入购物车
     */
    handleItemAdded = (event) => {
      const product = event.detail?.product;
      if (!product) return;

      const variantId = String(product.variant_id ?? product.id);

      if (variantId !== this.variantId) return;

      this.updateAdded(true);
      this.updateButtonState(true);
    };

    /**
     * 购物车变化（删除、修改数量等）
     */
    handleCartChange = (event) => {
      const variantIds = (event.detail?.variantIds || []).map(String);
      const added = variantIds.includes(this.variantId);

      this.updateAdded(added);
      this.updateButtonState(added);
    };

    getAddedList() {
      return JSON.parse(localStorage.getItem(PRODUCT_CARD_LOCK_KEY) || '[]');
    }

    setAddedList(list) {
      localStorage.setItem(PRODUCT_CARD_LOCK_KEY, JSON.stringify(list));
    }

    isAdded() {
      return this.getAddedList().includes(this.variantId);
    }

    updateAdded(added) {
      const list = this.getAddedList();

      if (added) {
        if (!list.includes(this.variantId)) {
          list.push(this.variantId);
        }
      } else {
        const index = list.indexOf(this.variantId);
        if (index > -1) {
          list.splice(index, 1);
        }
      }

      this.setAddedList(list);
    }

    updateButtonState(added) {
      const btn_text = this.querySelector('.text');
      if (added) {
        this.dataset.added = 'true';
        this.setAttribute('disabled', true);
        this.classList.add('is-disabled');
        /*
        btn_text.textContent = this.dataset.addedText || 'Added';*/
        btn_text.innerHTML = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2.21175 5.75028L4.53398 7.96192L9.06784 3.09631" stroke="white" stroke-width="1.99048" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
      } else {
        this.dataset.added = 'false';
        this.removeAttribute('disabled');
        this.classList.remove('is-disabled');

        if (this.dataset.stock !== 'false') {
          btn_text.textContent = this.dataset.defaultText || 'Add to cart';
        }
      }
    }
  }

  customElements.define('add-to-cart', AddToCart);
}


if (!customElements.get('content-tabs')) {
class ContentTabs extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('.tab-item').forEach((item) => {
      item.addEventListener('click', () => {
        this.update(item.dataset.index);
      });

      if (Shopify.designMode) {
        item.addEventListener('shopify:block:select', () => {
          this.update(item.dataset.index);
        });
      }
    });
  }

  update(index) {
    // 移除所有 tab active
    this.querySelectorAll('.tab-item.active')
      .forEach(item => item.classList.remove('active'));

    // 激活对应 tab
    this.querySelectorAll(`.tab-item[data-index="${index}"]`)
      .forEach(item => item.classList.add('active'));
    // 内容切换
    const currentPane = this.querySelector('.tab-pane.active');
    if (currentPane) {
      currentPane.classList.remove('active');
    }

    const pane = this.querySelector(`.tab-pane[data-index="${index}"]`);

    if (pane) {
      pane.classList.add('visible');
      requestAnimationFrame(() => {
        pane.classList.add('active');
      });
    }
  }
}
customElements.define('content-tabs', ContentTabs);
}

if (!customElements.get('copy-code')) {

class CopyCode extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.dataCode = this.dataset.code;
    this.codeId = this.dataset.codeId;
    this.url = this.dataset.url;
    this.autoRedirect = this.dataset.autoRedirect || false;
    this.delay = this.dataset.delay || 1500;

    if (!this.dataCode) {
      return;
    }

    this.addEventListener('click', () => this.copyToClipboard());
  }

  copyToClipboard() {
    const tempInput = document.createElement('input');
    tempInput.type = 'text';
    tempInput.readOnly = true;
    tempInput.value = this.dataCode;
    tempInput.style.position = 'absolute';
    tempInput.style.opacity = 0;

    document.body.appendChild(tempInput);

    tempInput.select();
    tempInput.setSelectionRange(0, 99999);
    document.execCommand('copy');

    document.body.removeChild(tempInput);

    this.showCopyMessage();
    this.delayedRedirect();
  }

  showCopyMessage() {
    const message = document.getElementById(`message--${this.codeId}`);

    if (!message) {
      return;
    }

    message.style.display = 'inline-flex';

    setTimeout(() => {
      message.style.display = 'none';
    }, this.delay);
  }

  delayedRedirect() {
    if (this.url && this.autoRedirect === 'true') {
      setTimeout(() => {
        window.location.href = this.url;
      }, this.delay);
    }
  }
}

customElements.define('copy-code', CopyCode);

}


/* 周倒计时，现在只用于rush Thursday的活动专题页 */
if (!customElements.get('countdown-weekly')) {
class CountdownWeekly extends HTMLElement {
  connectedCallback() {
    this.startDay =
      Number(this.dataset.startDay) ?? 2;

    this.durationDays =
      Number(this.dataset.durationDays) ?? 2;

    this.timezoneMode = this.dataset.timezoneOffset ?? 'local';

    this.numberMap = {
      days: {
        tens: this.querySelector(
          '[data-days-tens]'
        ),
        ones: this.querySelector(
          '[data-days-ones]'
        ),
      },

      hours: {
        tens: this.querySelector(
          '[data-hours-tens]'
        ),
        ones: this.querySelector(
          '[data-hours-ones]'
        ),
      },

      minutes: {
        tens: this.querySelector(
          '[data-minutes-tens]'
        ),
        ones: this.querySelector(
          '[data-minutes-ones]'
        ),
      },

      seconds: {
        tens: this.querySelector(
          '[data-seconds-tens]'
        ),
        ones: this.querySelector(
          '[data-seconds-ones]'
        ),
      },
    };

    this.update();

    this.interval = setInterval(() => {
      this.update();
    }, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  // 获取目标时区时间
  getTimezoneDate() {
    // 使用用户本地时间
    if (this.timezoneMode === 'local') {
      return new Date();
    }

    // 指定 UTC offset
    const offset =
      Number(this.timezoneMode);

    const now = new Date();

    const utc =
      now.getTime() +
      now.getTimezoneOffset() * 60000;

    return new Date(
      utc + offset * 3600000
    );
  }

  // 获取当前活动周期
  getCycle() {
    const now = this.getTimezoneDate();

    const currentDay = now.getDay();

    let diff =
      currentDay - this.startDay;

    if (diff < 0) {
      diff += 7;
    }

    const start = new Date(now);

    start.setDate(
      now.getDate() - diff
    );

    start.setHours(0, 0, 0, 0);

    const end = new Date(start);

    end.setDate(
      start.getDate() +
        this.durationDays
    );

    return {
      now,
      start,
      end,
    };
  }

  // 更新单组数字
  setNumber(type, value) {
    const els =
      this.numberMap[type];

    if (!els) return;

    const str = String(value)
      .padStart(2, '0');

    const [tens, ones] = str;

    if (
      els.tens.textContent !== tens
    ) {
      els.tens.textContent = tens;
    }

    if (
      els.ones.textContent !== ones
    ) {
      els.ones.textContent = ones;
    }
  }

  update() {
    const { now, start, end } =
      this.getCycle();

    const isActive =
      now >= start &&
      now < end;

    this.classList.toggle(
      'is-active',
      isActive
    );

    this.classList.toggle(
      'is-ended',
      !isActive
    );

    // 活动结束
    if (!isActive) {
      this.setNumber('days', 0);
      this.setNumber('hours', 0);
      this.setNumber('minutes', 0);
      this.setNumber('seconds', 0);

      return;
    }

    const diff = end - now;

    const totalSeconds =
      Math.floor(diff / 1000);

    const days = Math.floor(
      totalSeconds / 86400
    );

    const hours = Math.floor(
      (totalSeconds % 86400) /
        3600
    );

    const minutes = Math.floor(
      (totalSeconds % 3600) / 60
    );

    const seconds =
      totalSeconds % 60;

    this.setNumber(
      'days',
      days
    );

    this.setNumber(
      'hours',
      hours
    );

    this.setNumber(
      'minutes',
      minutes
    );

    this.setNumber(
      'seconds',
      seconds
    );
  }
}

customElements.define('countdown-weekly', CountdownWeekly);
}




     /*
*无法点击的选项。在切换变体的时候，会自动将其顺延到其他可选的选项
*/
let fixingInvalidVariant = false;

function fixDisabledSelectedRadios() {
  if (fixingInvalidVariant) return;

  const fieldsets = document.querySelectorAll(
    '.variations .product-form__input'
  );

  for (const fieldset of fieldsets) {
    const radios = [
      ...fieldset.querySelectorAll('input[type="radio"]')
    ];

    if (!radios.length) continue;

    // 当前选中的 radio
    const checkedRadio = radios.find(radio => radio.checked);

    if (!checkedRadio) continue;

    // 当前选中的值没有被禁用
    if (!checkedRadio.classList.contains('is-disabled')) {
      continue;
    }

    const currentIndex = radios.indexOf(checkedRadio);

    // 优先向后找
    const nextRadio = radios
      .slice(currentIndex + 1)
      .find(radio => !radio.classList.contains('is-disabled'));

    // 后面没有，再向前找
    const prevRadio = radios
      .slice(0, currentIndex)
      .reverse()
      .find(radio => !radio.classList.contains('is-disabled'));

    const targetRadio = nextRadio || prevRadio;

    if (!targetRadio) continue;

    console.log(
      '[Variant Fix]',
      fieldset.dataset.handle,
      'disabled:',
      checkedRadio.value,
      '→',
      targetRadio.value
    );

    fixingInvalidVariant = true;

    targetRadio.click();

    // 等这次 variant 更新完成后再允许下一次修正
    setTimeout(() => {
      fixingInvalidVariant = false;
    }, 100);

    return;
  }
}


/*
 * 1. variant-change 后检查
 */
document.addEventListener('product:variant-change', () => {
  // 立即检查一次
  fixDisabledSelectedRadios();

  // 再检查几次，覆盖主题异步更新 DOM 的情况
  requestAnimationFrame(() => {
    fixDisabledSelectedRadios();

    requestAnimationFrame(() => {
      fixDisabledSelectedRadios();
    });
  });
});


/*
 * 2. 监听 radio 的 class / checked / DOM 变化
 */
const variations = document.querySelector('.variations');

if (variations) {
  const observer = new MutationObserver(() => {
    fixDisabledSelectedRadios();
  });

  observer.observe(variations, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'checked']
  });
}

/* 用于页面的复制按钮，目前有使用的是产品页的coupon的显示。 */
function copyToClipboard(str) {
  var el = document.createElement('textarea');
  el.value = str;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  var selected =
      document.getSelection().rangeCount > 0
  ? document.getSelection().getRangeAt(0)
  : false;
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
  if (selected) {
    document.getSelection().removeAllRanges();
    document.getSelection().addRange(selected);
  }
}

function copyCode(e) {
  var $this = e.target;
  copyToClipboard($this.getAttribute('data-code'));
  $this.innerText = 'copied!';
  setTimeout(function() { $this.innerText = 'copy'; }, 1500);
}


// 获取 Omnisend 转盘的折扣码并存到 localStorage，同时显示在客户页面Coupon列表
(function () {
  const STORAGE_KEY = "omnisendDiscountCodes";
  const MS_IN_DAY = 24 * 60 * 60 * 1000;
  const VALID_DAYS = 30;

  // ---- Utils ----
  const safeParseJSON = (str, fallback = []) => {
    try {
      return JSON.parse(str) || fallback;
    } catch {
      return fallback;
    }
  };

  const formatDate = (timestamp) =>
    new Date(timestamp).toLocaleDateString("en-US");

  // ---- Storage ----
  function saveDiscountCode(email, code) {
    if (!email || !code) return;

    let storedCodes = safeParseJSON(localStorage.getItem(STORAGE_KEY));

    // 已存在则不重复存
    if (storedCodes.some((item) => item.code === code)) {
      console.log("⚠️ 折扣码已存在:", code);
      return;
    }

    storedCodes.push({ code, time: Date.now() });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storedCodes));
    console.log("✅ 已写入 localStorage:", storedCodes);

    renderDiscountCodes();
  }

  // ---- Render ----
  function renderDiscountCodes() {
    let storedCodes = safeParseJSON(localStorage.getItem(STORAGE_KEY));
    const now = Date.now();

    // 自动清理过期折扣码
    storedCodes = storedCodes.filter(
      (item) => now <= item.time + VALID_DAYS * MS_IN_DAY
    );
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storedCodes));

    storedCodes.forEach((item, index) => {
      const container = document.querySelector(
        `div[data-code-card="${item.code}"]`
      );
      if (!container) return;

      const startDate = formatDate(item.time);
      const endTime = item.time + VALID_DAYS * MS_IN_DAY;
      const endDate = formatDate(endTime);

      const { heading, discount, description } = container.dataset;

      container.style.display = "block";
      container.innerHTML = `
        <div class="card-wrapper">
          <div class="card-header card-item">${discount || ""}</div>
          <div class="card-content card-item">
            <h3 class="h6">${heading || ""}</h3>
            <div>${description || ""}</div>
            <div class="usage-time"><p>${startDate} - ${endDate}</p></div>
          </div>
          <div class="copy-button">
            <copy-code data-code="${item.code}" data-code-id="card-${index + 1}">Copy</copy-code>
          </div>
          <div id="message--card-${index + 1}" class="copy-message" style="display:none;">Copied!</div>
        </div>
      `;
    });
  }

  // ---- Intercept Fetch ----
  const originalFetch = window.fetch;
  window.fetch = async function (input, init) {
    if (
      typeof input === "string" &&
      input.includes("forms.soundestlink.com/REST/forms/v2/subscribe")
    ) {
      try {
        const email = init?.body ? JSON.parse(init.body).formData?.emailField : null;
        const response = await originalFetch(input, init);

        const data = await response.clone().json().catch(() => null);
        const discountCode = data?.discount?.code;

        saveDiscountCode(email, discountCode);

        return response;
      } catch (err) {
        console.error("❌ Omnisend 表单拦截失败:", err);
      }
    }
    return originalFetch(input, init);
  };

  // 页面加载时渲染 & 清理过期
  document.addEventListener("DOMContentLoaded", renderDiscountCodes);
})();
